import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';

/**
 * Hook to subscribe to real-time subscription updates via SSE
 * Automatically refreshes user data when subscription changes
 */
export function useSubscriptionEvents() {
  const { token, isAuthenticated, fetchCurrentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create SSE connection
    // Note: EventSource doesn't support custom headers, so we need to pass token via query
    // For better security, consider using fetch with ReadableStream instead
    const eventSource = new EventSource(`/api/v1/billing/events?token=${token}`);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('SSE message received:', data);

        if (data.type === 'subscription_updated') {
          console.log('Subscription updated, refreshing data...');
          // Refresh user data to get updated subscription status
          fetchCurrentUser();
          // Invalidate all billing-related query caches
          queryClient.invalidateQueries({ queryKey: ['billing'] });
        }
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      // EventSource will automatically try to reconnect
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [token, isAuthenticated, fetchCurrentUser, queryClient]);
}
